import React, { useState, useEffect, createContext } from "react"
import { Auth, Hub } from "aws-amplify"
import { CognitoUser } from "@aws-amplify/auth"
import { HubCallback } from "@aws-amplify/core/lib/Hub"

interface IAuthContext {
  currentUser: any
  isAuthenticated: boolean
  idToken: string | null
  accessToken: string | null
  refreshToken: string | null
  signIn(username: string): Promise<CognitoUser>
  signOut(): Promise<any>
  answerCustomChallenge(user: any, code: string): Promise<string>
}

const signIn = (username: string): Promise<CognitoUser> => {
  return Auth.signIn(username)
}

const signOut = (): Promise<any> => {
  return Auth.signOut()
}

const answerCustomChallenge = (user: any, code: string): Promise<string> => {
  return Auth.sendCustomChallengeAnswer(user, code)
}

const AuthContext = createContext<IAuthContext>({
  currentUser: null,
  isAuthenticated: false,
  idToken: null,
  accessToken: null,
  refreshToken: null,
  signIn,
  signOut,
  answerCustomChallenge,
})

const AuthProvider = (props: any) => {
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  const authListener: HubCallback = ({ payload: { event, data } }) => {
    switch (event) {
      case "signIn":
        Auth.currentUserInfo()
          .then(setCurrentUser)
          .catch(() => {})
        break
      case "signOut":
        setCurrentUser(null)
        setIdToken(null)
        setAccessToken(null)
        setIdToken(null)
        break
    }
  }

  useEffect(() => {
    setIsAuthenticated(currentUser !== null)
  }, [currentUser])

  useEffect(() => {
    props.onLoaded(isLoaded)
  }, [isLoaded])

  useEffect(() => {
    if (isLoaded === false) {
      Auth.currentSession()
        .then((session) => {
          if (session && session.isValid()) {
            setIdToken(session.getIdToken().getJwtToken())
            setAccessToken(session.getAccessToken().getJwtToken())
            setRefreshToken(session.getRefreshToken().getToken())
            Auth.currentUserInfo()
              .then((user) => {
                setCurrentUser(user)
                setIsLoaded(true)
              })
              .catch(() => {
                setIsLoaded(true)
              })
          } else {
            setIsLoaded(true)
          }
        })
        .catch(() => {
          setIsLoaded(true)
        })
    }
  }, [isLoaded])

  useEffect(() => {
    Hub.listen("auth", authListener)
    return () => Hub.remove("auth", authListener)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        currentUser: currentUser,
        isAuthenticated: isAuthenticated,
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken,
        signIn: signIn,
        signOut: signOut,
        answerCustomChallenge: answerCustomChallenge,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
